// src/pages/Register.tsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { register, clearError } from "../redux/slices/authSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Shield,
  Sparkles,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    password: "",
    password_confirmation: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, user, token, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );

  useEffect(() => {
    if (token && user && !user.email_verified_at) {
      navigate("/verify-email", { state: { email: user.email } });
      toast.success("Registration successful! Please verify your email.");
    }
  }, [token, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.email_verified_at) {
      navigate("/dashboard");
      toast.success("Registration complete! Welcome to ObeliskRecovery.");
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === "string" ? error : "Registration failed");
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const validatePassword = (password: string) => {
    const errors: string[] = [];
    const requirements = [
      { test: password.length >= 8, message: "At least 8 characters" },
      { test: /[A-Z]/.test(password), message: "One uppercase letter" },
      { test: /[a-z]/.test(password), message: "One lowercase letter" },
      { test: /\d/.test(password), message: "One number" },
      { test: /[!@#$%^&*]/.test(password), message: "One special character" },
    ];

    requirements.forEach((req) => {
      if (!req.test) errors.push(req.message);
    });

    setPasswordErrors(errors);
    setPasswordStrength(Math.floor(((5 - errors.length) / 5) * 100));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "password") {
      validatePassword(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.first_name ||
      !formData.last_name ||
      !formData.username ||
      !formData.email ||
      !formData.password ||
      !formData.password_confirmation
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (formData.password !== formData.password_confirmation) {
      toast.error("Passwords do not match");
      return;
    }

    if (passwordStrength < 60) {
      toast.error("Please use a stronger password (at least 60% strength)");
      return;
    }

    if (!termsAccepted) {
      toast.error("Please accept the Terms of Service and Privacy Policy");
      return;
    }

    const result = await dispatch(register(formData) as any);

    if (register.fulfilled.match(result)) {
      console.log("Registration successful, awaiting email verification");
    }
  };

  const getStrengthColor = (strength: number) => {
    if (strength < 40) return "from-red-500 to-red-400";
    if (strength < 80) return "from-yellow-500 to-yellow-400";
    return "from-green-500 to-green-400";
  };

  const getStrengthText = (strength: number) => {
    if (strength < 40) return "Weak";
    if (strength < 80) return "Medium";
    return "Strong";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 p-4">
      <div className="w-full max-w-2xl">
        {/* Floating Card */}
        <div className="relative">
          {/* Background Glow */}
          <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>

          {/* Main Card */}
          <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30">
            {/* Header */}
            <div className="text-center p-6 pb-8">
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <img
                    src={logo}
                    alt="ObeliskRecovery"
                    className="h-10 w-auto"
                  />
                  <div className="absolute -top-1 -right-1 h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                Create Account
              </h1>
              <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
                Join the secure crypto recovery platform
              </p>
            </div>

            {/* Form */}
            <div className="px-6 pb-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="group">
                    <label
                      htmlFor="first_name"
                      className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                    >
                      <span>First Name</span>
                      <Sparkles className="h-3 w-3 text-primary/60" />
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                        <User className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                      </div>
                      <input
                        id="first_name"
                        name="first_name"
                        type="text"
                        required
                        value={formData.first_name}
                        onChange={handleChange}
                        className={`${GlobalStyles.input} pl-10 focus:pl-11 transition-all duration-300`}
                        placeholder="John"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="group">
                    <label
                      htmlFor="last_name"
                      className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                    >
                      <span>Last Name</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                        <User className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                      </div>
                      <input
                        id="last_name"
                        name="last_name"
                        type="text"
                        required
                        value={formData.last_name}
                        onChange={handleChange}
                        className={`${GlobalStyles.input} pl-10 focus:pl-11 transition-all duration-300`}
                        placeholder="Doe"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>

                {/* Username */}
                <div className="group">
                  <label
                    htmlFor="username"
                    className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                  >
                    <span>Username</span>
                    <Sparkles className="h-3 w-3 text-primary/60" />
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <User className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                    </div>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      required
                      value={formData.username}
                      onChange={handleChange}
                      className={`${GlobalStyles.input} pl-10 focus:pl-11 transition-all duration-300`}
                      placeholder="johndoe"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="group">
                  <label
                    htmlFor="email"
                    className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                  >
                    <span>Email Address</span>
                    <Sparkles className="h-3 w-3 text-primary/60" />
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <Mail className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className={`${GlobalStyles.input} pl-10 focus:pl-11 transition-all duration-300`}
                      placeholder="you@example.com"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="group">
                  <label
                    htmlFor="password"
                    className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                  >
                    <span>Password</span>
                    <Sparkles className="h-3 w-3 text-primary/60" />
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <Lock className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className={`${GlobalStyles.input} pl-10 pr-10 focus:pl-11 transition-all duration-300`}
                      placeholder="Create a strong password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-primary hover:text-primary/70 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>

                  {/* Password Strength */}
                  {formData.password && (
                    <div className="mt-3 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Password Strength: {getStrengthText(passwordStrength)}
                        </span>
                        <span className="text-xs font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                          {passwordStrength}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${getStrengthColor(passwordStrength)} transition-all duration-500`}
                          style={{ width: `${passwordStrength}%` }}
                        ></div>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 mt-2">
                        {[
                          "8+ characters",
                          "Uppercase letter",
                          "Lowercase letter",
                          "Number",
                          "Special character",
                        ].map((req) => {
                          const isMet = !passwordErrors.includes(
                            req.split(" ")[1] === "+"
                              ? "At least 8 characters"
                              : req.includes("Uppercase")
                                ? "One uppercase letter"
                                : req.includes("Lowercase")
                                  ? "One lowercase letter"
                                  : req.includes("Number")
                                    ? "One number"
                                    : "One special character",
                          );
                          return (
                            <div
                              key={req}
                              className="flex items-center text-xs"
                            >
                              {isMet ? (
                                <CheckCircle className="h-3 w-3 text-green-500 mr-1.5 flex-shrink-0" />
                              ) : (
                                <XCircle className="h-3 w-3 text-red-500 mr-1.5 flex-shrink-0" />
                              )}
                              <span
                                className={
                                  isMet
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-gray-500"
                                }
                              >
                                {req}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="group">
                  <label
                    htmlFor="password_confirmation"
                    className={`${GlobalStyles.inputLabel} mb-2`}
                  >
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <Lock className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                    </div>
                    <input
                      id="password_confirmation"
                      name="password_confirmation"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      value={formData.password_confirmation}
                      onChange={handleChange}
                      className={`${GlobalStyles.input} pl-10 pr-10 focus:pl-11 transition-all duration-300 ${
                        formData.password_confirmation &&
                        formData.password !== formData.password_confirmation
                          ? "border-red-500 dark:border-red-500"
                          : formData.password_confirmation &&
                              formData.password ===
                                formData.password_confirmation
                            ? "border-green-500 dark:border-green-500"
                            : ""
                      }`}
                      placeholder="Confirm your password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-primary hover:text-primary/70 transition-colors"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {formData.password_confirmation &&
                    formData.password !== formData.password_confirmation && (
                      <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                        Passwords do not match
                      </p>
                    )}
                </div>

                {/* Terms Agreement */}
                <div className="flex items-start pt-2">
                  <input
                    id="terms"
                    name="terms"
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mt-0.5 flex-shrink-0"
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="terms"
                    className="ml-2 text-sm text-gray-600 dark:text-gray-400"
                  >
                    I agree to the{" "}
                    <Link
                      to="/terms"
                      className="text-primary hover:text-primary/80 font-medium"
                      target="_blank"
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy"
                      className="text-primary hover:text-primary/80 font-medium"
                      target="_blank"
                    >
                      Privacy Policy
                    </Link>
                  </label>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isLoading || !termsAccepted}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} w-full group/btn ${
                      !termsAccepted ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                        ></div>
                        Creating account...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <span>Create Account</span>
                        <svg
                          className="w-4 h-4 ml-2 opacity-0 group-hover/btn:opacity-100 transition-all duration-300 transform group-hover/btn:translate-x-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </div>
                    )}
                  </button>
                </div>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200/50 dark:border-gray-700/50"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white dark:bg-gray-800/90 text-gray-500 dark:text-gray-400">
                    Already have an account?
                  </span>
                </div>
              </div>

              {/* Login Link */}
              <Link
                to="/login"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full justify-center group/login`}
              >
                <span>Sign In Instead</span>
                <svg
                  className="w-4 h-4 ml-2 opacity-0 group-hover/login:opacity-100 transition-all duration-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
              </Link>

              {/* Security Badge */}
              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <Shield className="h-3 w-3 text-green-500" />
                <span>Enterprise-grade security & encryption</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
